import React, { useEffect } from 'react';
import { ClientContent, ClientHeader } from '@ecosystem/newron-client-lib';
import { CorePageProvider, useCorePageState } from './provider';
import { Heading } from './heading/heading';
import { UiActionsProvider } from '../ui.actions/provider';
import './page.css';
import { useParams } from 'react-router';
import { NavigateFunction } from 'react-router/dist/development/index';
import { FiltersPanel } from '../filters/panel';
import { FilterChips } from '../filters/chips';
import { ContextDisplay } from '../context/display';
import { Loading } from '../design/loading/Loading';
import { HTTPMethod } from '../types';
import { CoreModal } from '../modal/modal';
import { AnalyticsProvider } from '../analytics/analytics.provider';
import { useTrackPageViewed } from '../analytics/trackPageViewed.hook';
import { DataErrorElement } from '../elements/error/error';
import { PageInfo, UpsellContext } from './types';
import PageFooter from './footer';
import { getTrackingTagsFn } from '../analytics/tools';
import { useTracking } from '../analytics/tracking.hook';
import { MappingFn } from '../analytics/types';
import { ContextManager } from '../context/manager';
import { UniquePreferencesProvider } from '../context/unique-preferences/unique-preferences.provider';
import { MobileProvider, useMobile } from '../context/mobile/mobile.provider';
import MainSection from './mainSection';
import classNames from 'classnames';
import { ReactQueryProvider } from './ReactQueryProvider';
import { shouldDisableFilterTray } from './heading/utils';

type CorePageProps = {
  context: {
    name: string;
    url: string;
    excludeFilters?: boolean;
    excludePriceFilter?: boolean;
  };
  page: PageInfo;
  data: {
    url: string;
    requestMethod?: HTTPMethod;
  };
  collection: {
    url: string;
  };
  navigate: NavigateFunction;
  defaultSubsection: string;
  calcTrackingParams: MappingFn;
  upsellContext: UpsellContext;
};

// this is the main component that renders the page and render triggered by the route in the MFE repo
function CorePage({
  context,
  page,
  data,
  navigate,
  collection,
  calcTrackingParams,
  defaultSubsection,
  upsellContext,
}: CorePageProps) {
  const { tab } = useParams();
  const { trackEvent } = useTracking(calcTrackingParams);
  const getTrackingTags = getTrackingTagsFn(defaultSubsection, tab, true, calcTrackingParams);
  return (
    <ReactQueryProvider>
      <UniquePreferencesProvider>
        <MobileProvider>
          <CorePageProvider
            page={page}
            context={context}
            data={data}
            collection={collection}
            upsellContext={upsellContext}
          >
            <AnalyticsProvider externalTrackingFn={trackEvent} externalGetTrackingTags={getTrackingTags}>
              <UiActionsProvider navigate={navigate}>
                <ClientHeader>
                  <ContextDisplay />
                </ClientHeader>
                <PageComponent />
              </UiActionsProvider>
            </AnalyticsProvider>
          </CorePageProvider>
        </MobileProvider>
      </UniquePreferencesProvider>
    </ReactQueryProvider>
  );
}

function PageComponent() {
  const {
    dsl,
    error,
    dslLoading,
    contextLoading,
    updatePageSettings,
    page,
    pageParams,
    modalDsl,
    globalWarnings,
    pageWarnings,
    closeModal,
    allComponentsHaveNoData,
    resetGlobalWarnings,
    trayType,
  } = useCorePageState();

  useTrackPageViewed();
  const showPageLevelNoDataWarning = Boolean(dsl?.settings?.enablePageLevelNoDataError && allComponentsHaveNoData);

  const { isMobile } = useMobile();

  const contextSubscriber = () => {
    resetGlobalWarnings();
  };

  useEffect(() => {
    ContextManager.getInstance().subscribeToSettingsChanges(contextSubscriber);

    return () => {
      ContextManager.getInstance().unsubscribeFromSettingsChanges(contextSubscriber);
    };
  }, []);

  return error ? (
    <ErrorPageComponent error={error} />
  ) : (
    <>
      <ClientContent>
        <div className={classNames('core-page', { 'mobile-page': isMobile })}>
          <Loading isActive={dslLoading || contextLoading} withIndicator />
          <div
            className={classNames('core-page-container flex flex-column px-6', {
              'mobile-page-container': isMobile,
            })}
          >
            {dsl?.heading && (
              <Heading pageParams={pageParams} updateSettings={updatePageSettings} heading={dsl.heading} page={page} />
            )}
            <div className="core-page-content flex-grow w-full max-w-full">
              {!shouldDisableFilterTray(dsl) && <FilterChips />}

              <MainSection
                dsl={dsl}
                showPageLevelNoDataWarning={showPageLevelNoDataWarning}
                globalWarnings={globalWarnings}
                pageWarnings={pageWarnings}
              />
            </div>
            <PageFooter className={classNames('core-page-footer', { 'mobile-footer': isMobile })} />
          </div>
        </div>
        {!trayType && <FiltersPanel />}
      </ClientContent>
      {modalDsl && <CoreModal dsl={modalDsl} onClose={closeModal} />}
    </>
  );
}

const ErrorPageComponent: React.FC<{ error: string }> = ({ error }) => (
  <ClientContent>
    <div className="core-page relative">
      <div className="core-page-container relative flex flex-column px-6">
        <div className="core-page-content relative flex-grow w-full max-w-full">
          <FilterChips />
          {error && <DataErrorElement error={{ message: error, className: 'pt-10', title: 'Unable to load page' }} />}
        </div>
        <PageFooter className="core-page-footer" />
      </div>
    </div>
    <FiltersPanel />
  </ClientContent>
);

export { CorePage, PageComponent };
export type { CorePageProps };
